import { useEffect, useRef, useState } from 'react';

/**
 * The Cloudflare Turnstile challenge, rendered only when the server asks for one.
 *
 * The server decides IF a challenge is needed and hands back the public site key with the
 * refusal, so this component never carries its own copy of the key and never has an opinion
 * about when to appear.
 *
 * Rendered explicitly rather than by auto-scan: the widget appears mid-session, after the form
 * is already on screen, so there is nothing for an on-load scan to find.
 *
 * NOTE ON FAILURE: if the script cannot load — an extension blocked it, the network is down —
 * there is no way past the challenge, on this surface or on the server-rendered login. So the
 * failure is SAID OUT LOUD through `onUnavailable` rather than leaving a silent empty gap that
 * looks like the page is still thinking.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

type TurnstileApi = {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      theme?: 'light' | 'dark' | 'auto';
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Load the script once per document, and resolve when the API is actually usable. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');

    const settle = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('turnstile_unavailable'));
    };

    script.addEventListener('load', settle, { once: true });
    script.addEventListener('error', () => reject(new Error('turnstile_unavailable')), {
      once: true,
    });

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onUnavailable,
}: {
  siteKey: string;
  /** Called with a fresh token, and with '' whenever the token stops being valid. */
  onToken: (token: string) => void;
  onUnavailable: () => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  // Ref, not state: the cleanup needs the id without re-running this effect.
  const widgetIdRef = useRef<string | null>(null);
  const [, setRendered] = useState(false);

  // The callbacks live in refs so the render effect can depend on siteKey ALONE and still always
  // call the latest ones. Listing them as dependencies instead would tear the widget down and
  // rebuild it every time the parent re-renders with a new inline arrow — which is every
  // keystroke in the form — destroying a challenge the user is part-way through solving.
  const onTokenRef = useRef(onToken);
  const onUnavailableRef = useRef(onUnavailable);
  useEffect(() => {
    onTokenRef.current = onToken;
    onUnavailableRef.current = onUnavailable;
  });

  useEffect(() => {
    let cancelled = false;

    loadTurnstile()
      .then((api) => {
        if (cancelled || !hostRef.current) return;
        widgetIdRef.current = api.render(hostRef.current, {
          sitekey: siteKey,
          // The signed-out surface is always dark, so the widget matches rather than punching a
          // light rectangle into the card.
          theme: 'dark',
          callback: (token) => onTokenRef.current(token),
          // A token that errored or expired is no longer a token. Clear it so a stale one is
          // never submitted and silently rejected.
          'error-callback': () => onTokenRef.current(''),
          'expired-callback': () => onTokenRef.current(''),
        });
        setRendered(true);
      })
      .catch(() => {
        if (!cancelled) onUnavailableRef.current();
      });

    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          // Already gone with the unmounted DOM. Nothing to clean up.
        }
      }
    };
  }, [siteKey]);

  return <div ref={hostRef} data-testid="turnstile-host" />;
}
